import { pb } from '@/core/api';
import type { Creator } from '@/types';

// Excludes voided/soft-deleted/AI-rejected leads — the old export applied
// no filter at all and shipped the raw table, including admin-sanctioned,
// collector-trashed, and AI-rejected rows. Matches the leading columns of
// idx_leads_status_created (deleted_at, ai_analysis_status, created DESC)
// so it stays index-friendly at scale.
const VALID_FILTER =
  'is_void = false && deleted_at = "" && user_deleted_at = "" && ' +
  'ai_analysis_status != "rejected_by_ai"';

const EXPORT_FIELDS = [
  'id',
  'ig_username',
  'display_name',
  'followers',
  'email',
  'creator_type',
  'category',
  'rank',
  'sex',
  'status',
  'bio',
  'notes',
  'post_url',
  'created',
].join(',');

// PocketBase hard-caps perPage at 1000 on the standard list endpoint
// regardless of what's requested (verified empirically against staging —
// asking for 2000 or 4000 silently comes back clamped to 1000). Concurrency
// above 6 caused requests to fail outright when tested against staging with
// ~81k matching rows; 6 completed reliably (~82 pages, ~2 minutes total).
const PAGE_SIZE = 1000;
const CONCURRENCY = 6;

function fetchPage(page: number): Promise<Creator[]> {
  return pb
    .collection('leads')
    .getList<Creator>(page, PAGE_SIZE, {
      filter: VALID_FILTER,
      sort: '-created',
      fields: EXPORT_FIELDS,
    })
    .then((res) => res.items);
}

async function fetchRemainingPages(totalPages: number): Promise<Creator[]> {
  const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
  const all: Creator[] = [];
  for (let i = 0; i < pages.length; i += CONCURRENCY) {
    const batch = pages.slice(i, i + CONCURRENCY);
    const results = await Promise.all(batch.map(fetchPage));
    results.forEach((items) => all.push(...items));
  }
  return all;
}

// Fired in concurrent windows instead of the SDK's getFullList, which
// pages sequentially one request at a time (~200 round trips at 100k
// rows). Same standard rule-evaluated collection API the creators list
// page uses — no custom listRule-bypass route (see use-creators.ts's
// history note on why that pattern was reverted after three regressions).
export async function fetchAllValidCreators(): Promise<Creator[]> {
  const first = await pb.collection('leads').getList<Creator>(1, PAGE_SIZE, {
    filter: VALID_FILTER,
    sort: '-created',
    fields: EXPORT_FIELDS,
  });
  const rest = await fetchRemainingPages(first.totalPages);
  return [...first.items, ...rest];
}
